import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { hasPermission } from "../../utils/helper";
import { PERMISSION } from "../../utils/constants";
import CourierListPage, { CourierListHeadAction } from "./courier-list-page";
import ManualJobForm from "./forms/manual-job-form";
import AssignTripForm from "./forms/assign-trip-form";
import CancelTripForm from "./forms/cancel-trip-form";
import Confirmation from "../commons/confirmation";
import CourierTable from "./ui/courier-table";
import CourierAction from "./ui/courier-action";
import TripProgressModal from "./ui/trip-progress-modal";
import { useCourierMutationDone } from "./ui/use-courier-mutation-done";

const todayISO = () => new Date().toISOString().slice(0, 10);

const TripsList = props => {
  const {
    getCourierTrips,
    trips = [],
    courierLoading,
    generateCourierTrips,
    getCourierTripTasks,
    tripTasks = [],
    getEligibleCouriers,
    eligibleCouriers = [],
    assignCourierTrip,
    cancelCourierTrip,
    getCourierCenters,
    centers = [],
    createCourierManualJob,
    autoAssignCourierTrip,
    resetCourierState,
    courierApiError
  } = props;

  const [dateFilter, setDateFilter] = useState("");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [assignTrip, setAssignTrip] = useState(null);
  const [cancelTrip, setCancelTrip] = useState(null);
  const [isOpenManualModal, setIsOpenManualModal] = useState(false);
  const [confirmGenerate, setConfirmGenerate] = useState(false);

  const canManageMasters = hasPermission(PERMISSION.CAN_MANAGE_COURIER_MASTERS);
  const canAssign = hasPermission(PERMISSION.CAN_ASSIGN_COURIER_ROUND);
  const canCancel = hasPermission(PERMISSION.CAN_MANAGE_COURIERS);
  const canCreateManual = hasPermission(
    PERMISSION.CAN_CREATE_COURIER_MANUAL_ORDER
  );

  useCourierMutationDone(courierLoading, courierApiError, () => {
    setIsOpenManualModal(false);
    setAssignTrip(null);
    setCancelTrip(null);
  });

  useEffect(() => {
    getCourierTrips({});
    if (canCreateManual && getCourierCenters) getCourierCenters({});
    return () => {
      resetCourierState();
    };
  }, []);

  const applyFilter = () =>
    getCourierTrips(dateFilter ? { tripDate: dateFilter } : {});

  const onGenerate = () => {
    generateCourierTrips(
      dateFilter ? { tripDate: dateFilter } : { tripDate: todayISO() }
    );
    setConfirmGenerate(false);
  };

  const openTasks = trip => {
    setSelectedTrip(trip);
    setAssignTrip(null);
    getCourierTripTasks(trip.id);
  };

  const openAssign = trip => {
    setAssignTrip(trip);
    setSelectedTrip(null);
    getEligibleCouriers(trip.id);
  };

  return (
    <CourierListPage
      title="Courier Trips"
      count={trips.length}
      loading={courierLoading}
      headActions={
        <>
          {canManageMasters && (
            <CourierListHeadAction
              label="Generate Trips"
              onClick={() => setConfirmGenerate(true)}
            />
          )}
          {canCreateManual && (
            <CourierListHeadAction
              label="New Manual Job"
              onClick={() => setIsOpenManualModal(true)}
            />
          )}
        </>
      }
    >
      <div className="list-search-area courier-trips-search">
        <div className="courier-toolbar">
          <label className="courier-toolbar-label">
            Trip date
            <input
              type="date"
              value={dateFilter}
              onChange={e => setDateFilter(e.target.value)}
            />
          </label>
          <button type="button" className="create-btn courier-toolbar-btn" onClick={applyFilter}>
            Apply
          </button>
          <button
            type="button"
            className="courier-btn-secondary"
            title="Refresh list"
            onClick={applyFilter}
          >
            Refresh
          </button>
        </div>
      </div>

      <CourierTable>
        <thead>
          <tr>
            <th>Code</th>
            <th>Date</th>
            <th>Round</th>
            <th>Courier</th>
            <th>Status</th>
            <th>Tasks</th>
            <th className="act act-wide">Actions</th>
          </tr>
        </thead>
        <tbody>
          {trips.map(t => (
            <tr key={t.id}>
              <td>{t.code}</td>
              <td>{t.tripDate}</td>
              <td>{t.roundCode}</td>
              <td>{t.courierName || "—"}</td>
              <td>{t.statusDisplay}</td>
              <td>{t.tasksCount}</td>
              <td className="act act-wide">
                <span className="courier-row-actions">
                  <CourierAction
                    title="Progress"
                    iconClass="icon-edit"
                    onClick={() => openTasks(t)}
                  />
                  <CourierAction
                    title="Assign courier"
                    iconClass="icon-rider"
                    onClick={() => openAssign(t)}
                    disabled={!canAssign}
                    disabledTitle={`Requires permission: ${PERMISSION.CAN_ASSIGN_COURIER_ROUND}`}
                  />
                  <CourierAction
                    title="Auto-assign"
                    iconClass="icon-sync"
                    onClick={() => autoAssignCourierTrip(t.id)}
                    disabled={!canAssign}
                    disabledTitle={`Requires permission: ${PERMISSION.CAN_ASSIGN_COURIER_ROUND}`}
                  />
                  <CourierAction
                    title="Cancel trip"
                    iconClass="icon-cancel"
                    onClick={() => setCancelTrip(t)}
                    disabled={!canCancel}
                    disabledTitle={`Requires permission: ${PERMISSION.CAN_MANAGE_COURIERS}`}
                  />
                </span>
              </td>
            </tr>
          ))}
          {!trips.length && !courierLoading && (
            <tr>
              <td colSpan={7}>No trips found.</td>
            </tr>
          )}
        </tbody>
      </CourierTable>

      {selectedTrip && (
        <Modal
          isOpen={!!selectedTrip}
          onRequestClose={() => setSelectedTrip(null)}
          className="courier-wide-modal"
        >
          <TripProgressModal
            trip={selectedTrip}
            tasks={tripTasks}
            loading={courierLoading}
            onClose={() => setSelectedTrip(null)}
          />
        </Modal>
      )}

      {isOpenManualModal && (
        <Modal
          isOpen={isOpenManualModal}
          onRequestClose={() => setIsOpenManualModal(false)}
          className="small-modal"
        >
          <ManualJobForm
            closeModal={() => setIsOpenManualModal(false)}
            loading={courierLoading}
            centers={centers}
            apiError={courierApiError}
            createCourierManualJob={createCourierManualJob}
          />
        </Modal>
      )}

      {assignTrip && (
        <Modal
          isOpen={!!assignTrip}
          onRequestClose={() => setAssignTrip(null)}
          className="small-modal"
        >
          <AssignTripForm
            closeModal={() => setAssignTrip(null)}
            loading={courierLoading}
            trip={assignTrip}
            eligibleCouriers={eligibleCouriers}
            apiError={courierApiError}
            assignCourierTrip={assignCourierTrip}
          />
        </Modal>
      )}

      {cancelTrip && (
        <Modal
          isOpen={!!cancelTrip}
          onRequestClose={() => setCancelTrip(null)}
          className="small-modal"
        >
          <CancelTripForm
            closeModal={() => setCancelTrip(null)}
            loading={courierLoading}
            trip={cancelTrip}
            apiError={courierApiError}
            cancelCourierTrip={cancelCourierTrip}
          />
        </Modal>
      )}

      {confirmGenerate && (
        <Modal
          isOpen={confirmGenerate}
          onRequestClose={() => setConfirmGenerate(false)}
          className="small-modal"
        >
          <Confirmation
            text={`Generate trips for ${dateFilter || todayISO()}?`}
            onConfirm={onGenerate}
            onClose={() => setConfirmGenerate(false)}
          />
        </Modal>
      )}
    </CourierListPage>
  );
};

export default TripsList;
